import { Attrs, EventListeners, Minze, MinzeElement, Reactive } from 'minze'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'

Locale.getInstance().registerKeySet('fr', 'lvcAccount', {
  title: 'Bienvenue à La&nbsp;Vitrine!',
  subtitle: 'Faisons connaissance',
  validate: 'Valider',
  skipProfileText:
    'Nous vous invitons à compléter ces informations afin que vous puissiez profiter pleinement de la plateforme et de recommandations personnalisées selon vos goûts.',
  skipProfileBtn: 'Sauter',
  sidebar: {
    title: 'La Vitrine Culturelle est une organisation à but non lucratif québécoise.',
    into: "En créant un profil vous recevrez des suggestions d'activités selon vos goûts.",
    description:
      "Vos renseignements personnels resteront sous notre garde, ne seront partagés avec personne et ne seront jamais vendus. Ils ne serviront qu'à accomplir notre mission : promouvoir et vous faire découvrir des activités qui se déroulent au Québec. Vous pourrez en tout temps demander de supprimer ou de modifier votre profil Vitrine en vous connectant à votre compte.",
  },
  becomeProNotice: {
    title: `Pour créer un événement pour un artiste ou une organisation, passez par ici: 
    <a    
    id="link-pro-account"
    href="${MainModule.getInstance().getRouting().getRoute('partnersPage')}" 
    class="link" 
    target="_blank"
    >
    Créer un compte pro
    </a>`,
  },
})

Locale.getInstance().registerKeySet('en', 'lvcAccount', {
  title: 'Welcome to La&nbsp;Vitrine!',
  subtitle: "Let's get to know each other",
  validate: 'Validate',
  skipProfileText:
    'We recommend that you complete your profile in order to take full advantage of the personalized suggestions offered on the platform.',
  skipProfileBtn: 'Skip',
  sidebar: {
    title: 'La Vitrine Culturelle is a Quebec non-profit organization.',
    into: 'By creating a profile you will receive activity suggestions according to your tastes.',
    description:
      'Your personal information will remain in our custody, will not be shared with anyone and will never be sold. They will only be used to accomplish our mission: to promote and introduce you to activities taking place in Quebec. You can request to delete or modify your Vitrine profile at any time by logging into your account.',
  },
  becomeProNotice: {
    title: `To create an events for an artist or organization, click here: 
    <a 
    id="link-pro-account"
    href="${MainModule.getInstance().getRouting().getRoute('partnersPage')}" 
    class="link" 
    target="_blank"
    >
    access the Pro Zone
    </a>`,
  },
})

export interface LvcAccountWrapper {
  isCompletedAccount: boolean
  isCompleteAccountSkipped: boolean
  showBecomePartnerButton: boolean
  loading: boolean
  currentRoute: string
  disableUserMenu?: boolean
  isInB2bApp?: boolean
  onLogoutRedirectUrl?: string
}

export class LvcAccountWrapper extends MinzeElement {
  _authHandler: any = null

  attrs: Attrs = ['disable-user-menu', 'is-in-b2b-app', 'on-logout-redirect-url']

  reactive: Reactive = [
    ['isCompletedAccount', false],
    ['isCompleteAccountSkipped', false],
    ['showBecomePartnerButton', false],
    ['loading', true],
    ['currentRoute', window.location.search],
    ['isProTheme', false],
  ]

  handleAuthChanged = async (data: any): Promise<void> => {
    const mainModule = MainModule.getInstance()
    const user = await mainModule.getAuth().getUserData()
    const isPartner = await mainModule.getAuth().isPartner()
    const isPartnerInProcess = await mainModule.getAuth().isPartnerInProcess()

    if (data?.isLoggedIn) {
      if (isPartner || isPartnerInProcess) this.showBecomePartnerButton = false
      this.isCompletedAccount = user?.profile?.userProcess?.signupCompleted
      this.isCompleteAccountSkipped = user?.profile?.userProcess?.signupCompletionSkipped // check if the user has skipped the account completion
      this.showBecomePartnerButton = !(isPartner || isPartnerInProcess || !this.isCompletedAccount)

      this.loading = false
    }
  }

  trackProAccountClick = async () => {
    const emplacement = this.isCompletedAccount ? 'page_profil' : 'sign_up_funnel_completion'

    await MainModule.getInstance().analyticsTrack('cta_pro_redirect', {
      emplacement,
    })
  }

  validate = async () => {
    Minze.cast('lvc:validate-profile-completion', {
      isNewAccount: true,
    })
  }

  // function to skip the account completion
  skipAccountCompletion = async () => {
    Minze.cast('lvc:skip-profile-completion')
  }

  async onReady(): Promise<void> {
    const mainModule = MainModule.getInstance()
    this._authHandler = this.handleAuthChanged.bind(this)
    mainModule.getEE().on('lvc:user_state_changed', this._authHandler.bind(this))
    this.handleAuthChanged({
      isLoggedIn: await mainModule.getAuth().isLoggedIn(),
    })
  }

  html = () => `
    <div class="account-wrapper ${this.isPartner ?? 'theme-pro'}">
      <div class="account-breadcrumbs">
        <slot name="breadcrumb"></slot>
      </div>
      <div class="account-container">
        <div class="account-sidebar">
          ${
            !this.loading
              ? /* html */ `
            ${
              this.isCompletedAccount || this.isCompleteAccountSkipped
                ? /* html */ `
              <lvc-account-nav ${this.disableUserMenu ? 'disable-user-menu' : ''} ${this.isInB2bApp ? 'is-in-b2b-app' : ''} ${this.onLogoutRedirectUrl ? `on-logout-redirect-url="${this.onLogoutRedirectUrl}"` : ''}></lvc-account-nav>
            `
                : /* html */ `
              <lvc-hand-drawn-box variant="box2">
                <div class="account-sidebar-content">
                  <h2 class="account-sidebar-title">${Locale.getKey('lvcAccount.sidebar.title')}</h2>
                  <p class="account-sidebar-into">${Locale.getKey('lvcAccount.sidebar.into')}</p>
                  <p class="account-sidebar-description">${Locale.getKey('lvcAccount.sidebar.description')}</p>
                </div>
              </lvc-hand-drawn-box>
            `
            }
          `
              : /* html */ `
            <lvc-hand-drawn-box variant="box2">
              <div style="width:320px; height:480px;"></div>
            </lvc-hand-drawn-box>
          `
          }
        </div>
        <div class="account-main">
          ${
            !this.loading
              ? /* html */ `
            ${
              this.isCompletedAccount || this.isCompleteAccountSkipped
                ? /* html */ `
              <lvc-hand-drawn-box bgColor="${this.isProTheme ? '#BFF0FF' : '#DCFFD7'}" borderColor="#1E007D">
                <slot name="main"></slot>
              </lvc-hand-drawn-box>
            `
                : /* html */ `
              <lvc-hand-drawn-box>
                <div class="account-complete-profile">
                  <div class="account-complete-profile-header">
                    <div class="account-complete-profile-header-charlie">
                      <lvc-charlie-hands-together-full-body width="100%" height="auto"></lvc-charlie-hands-together-full-body>
                    </div>
                    <div class="account-complete-profile-header-content">
                      <div class="account-complete-profile-header-subtitle">${Locale.getKey('lvcAccount.subtitle')}</div>
                      <h2 class="account-complete-profile-header-title">${Locale.getKey('lvcAccount.title')}</h2>
                    </div>
                  </div>
                  <p class="account-skip-complete-profile-subtitle">${Locale.getKey('lvcAccount.skipProfileText')}</p>
                  <lvc-account-info
                    title-separator="true"
                    fields="firstName,lastName,zipcode,location"
                    submit-button="false"
                  ></lvc-account-info>
                  <lvc-account-cultural-profile
                    title-separator="true"
                    show-toasts="false"
                  ></lvc-account-cultural-profile>
                  <lvc-account-notifications
                    title-separator="true"
                    show-toasts="false"
                    component-location="account-complete-profile"
                  ></lvc-account-notifications>
                  <div class="account-complete-profile-footer">
                    <lvc-button
                      display="flex"
                      align-items="center"
                      justify-content="center"
                      width="100%"
                      padding="15px 25px"
                      id="btn-validate"
                    >
                      ${Locale.getKey('lvcAccount.validate')}
                      <lvc-chevron-right-icon></lvc-chevron-right-icon>
                    </lvc-button>

                    <lvc-button
                      display="flex"
                      align-items="center"
                      justify-content="center"
                      background="transparent"
                      color="#1E007D"
                      border="1px solid #1E007D"
                      width="100%"
                      padding="15px 25px"
                      id="btn-skip-profile"
                    >
                      ${Locale.getKey('lvcAccount.skipProfileBtn')}
                    </lvc-button>                  
                  </div>
                </div>
              </lvc-hand-drawn-box>
              <div class="account-complete-profile-delete">
                <lvc-account-button-delete></lvc-account-button-delete>
              </div>
            `
            }
          `
              : /* html */ `
            <lvc-hand-drawn-box>
              <div style="height:480px;"></div>
            </lvc-hand-drawn-box>
          `
          }
        </div>
      </div>
      ${
        this.showBecomePartnerButton
          ? /* html */ `
          <div class="account-footer">
            <p>${Locale.getKey('lvcAccount.becomeProNotice.title')}</p>
          </div>
        `
          : ''
      }
      <div class="account-charlie">
        <lvc-random-charlie></lvc-random-charlie>
      </div>
    </div>
  `

  eventListeners: EventListeners = [
    [window, 'lvc:user_state_changed', this.handleAuthChanged.bind(this)],
    ['#btn-validate', 'click', this.validate.bind(this)],
    ['#link-pro-account', 'click', this.trackProAccountClick.bind(this)],
    ['#btn-skip-profile', 'click', this.skipAccountCompletion.bind(this)],
  ]

  css = () => `
    :host {
      min-height: calc(100vh - 89.59px);
      position: relative;
      background: ${
        this.isPartner
          ? `linear-gradient(to top left, #BFF0FF, rgba(255, 153, 150, 0), #889CFE),
          linear-gradient(to top right, #BFF0FF,rgba(255, 153, 150, 0), #14C2BF) rgba(191,240,255,100);`
          : `linear-gradient(to top left, #f3fdaa, rgba(255, 153, 150, 0), #00aebf),
          linear-gradient(to top right, #fdecd8,rgba(255, 153, 150, 0), #1ade96) rgba(191,240,255,100);`
      };
      overflow: hidden;
      padding-top: 2rem;
      padding-bottom: 2rem;
    }

    .account-wrapper {
      position: relative;
      max-width: 1440px;
      margin-left: auto;
      margin-right: auto;
      width: 100%;
      font-size: 16px;
      padding-left: 2rem;
      padding-right: 2rem;
    }

    .account-breadcrumbs {
      margin-bottom: 20px;
    }

    .account-container {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 10px;
      z-index: 1;
    }

    .account-sidebar {}

    .account-sidebar-content {
      display: flex;
      flex-direction: column;
      gap: 20px;
      width: 100%;
      padding: 20px;
    }

    .account-sidebar-title {
      margin: 0;
      color: #1E007D;
      font-size: 1.125em;
      font-weight: 800;
      line-height: 120%;
    }

    .account-sidebar-into {
      margin: 0;
      color: #1E007D;
      font-size: 1em;
      font-weight: 800;
      line-height: 120%;
    }

    .account-sidebar-description {
      margin: 0;
      color: #1E007D;
      font-size: 0.750em;
      font-weight: 500;
    }

    .account-main {
      flex: 1 1 auto;
    }

    ::slotted([slot=main]),
    .account-complete-profile {
      padding: 20px;
    }

    .account-complete-profile {
      display: flex;
      flex-direction: column;
      gap: 30px;
    }

    .account-complete-profile-header {
      display: flex;
      flex-direction: column;
    }

    .account-complete-profile-header-content {
      text-align: center;
    }

    .account-complete-profile-header-subtitle {
      margin: 0 0 5px;
      color: #1E007D;
      font-size: 1.125em;
      font-weight: 800;
      line-height: 120%;
    }

    .account-complete-profile-header-title {
      margin: 0;
      color: #1E007D;
      font-size: 2.125em;
      font-weight: 800;
      line-height: 120%;
    }

    .account-skip-complete-profile-subtitle {
      margin: 0;
      color: #1E007D;
      font-size: 0.750em;
      font-weight: 500;
      line-height: 120%;
    }

    .account-complete-profile-header-charlie {
      max-width: 160px;
      margin: 0 auto;
    }

    .account-complete-profile-footer {
      display: flex;
      flex-direction: column;
      gap: 20px;
      align-items: center;
      justify-content: center;
    }

    .account-complete-profile-delete {
      margin: 10px 0;
    }

    lvc-chevron-right-icon {
      width: 24px;
      height: 24px;
      margin-left: 5px;
    }

    .account-footer {
      margin-top: 20px;
      font-size: 0.750em;
      font-weight: 500;
      line-height: 120%;
      text-align: center;
    }

    .account-footer p,
    .account-footer .link {
      margin: 0;
      color: #71A06A;
    }

    .account-charlie {
      margin-bottom: -40px;
    }
    
    @media screen and (max-width: 991px) {
      lvc-button {
        width: 100%;
      }
      .account-wrapper {
        padding-left: 1rem;
        padding-right: 1rem;
      }
    }

    @media only screen and (min-width: 992px) {
      .account-breadcrumbs {
        margin-bottom: 30px;
      }

      .account-container {
        flex-direction: row;
        gap: 32px;
      }

      .account-sidebar {
        margin-bottom: 30px;
      }

      .account-sidebar-content {
        gap: 30px;
        width: 320px;
        min-height: 480px;
        padding: 40px 20px;
      }

      .account-sidebar-title {
        font-size: 1.500em;
      }

      .account-sidebar-description {
        font-size: 0.875em;
      }

      .account-main {
        margin-bottom: 30px;
      }

      ::slotted([slot=main]) {
        padding: 80px;
      }

      .account-complete-profile {
        gap: 50px;
        padding: 80px 80px 50px;
      }

      .account-complete-profile-header {
        flex-direction: row;
        align-items: center;
        gap: 40px;
      }

      .account-complete-profile-header-content {
        max-width: 520px;
        text-align: left;
      }

      .account-complete-profile-header-subtitle {
        margin-bottom: 20px;
        font-size: 2em;
        line-height: 1;
      }

      .account-complete-profile-header-title {
        font-size: 4.5em;
        line-height: 1;
      }

      .account-skip-complete-profile-subtitle {
        font-size: 0.875em;
      }

      .account-complete-profile-header-charlie {
        max-width: 240px;
        margin: 0;
      }

      .account-complete-profile-delete {
        display: flex;
        justify-content: end;
        margin: 20px 80px;
      }

      .account-footer {
        margin-top: 50px;
        text-align: right;
      }

      .account-charlie {
        position: absolute;
        bottom: -40px;
        left: 100px;
        margin-bottom: 0px;
      }
    }
  `
}
